import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, SearchX } from 'lucide-react';
import { cn } from 'lib/utils';

interface SearchSelectOption {
    value: string | number;
    label: string;
    subLabel?: string;
}

interface SearchSelectProps {
    options: SearchSelectOption[];
    value?: string | number;
    onChange: (value: string | number) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    disabled?: boolean;
    error?: string;
    label?: string;
    required?: boolean;
    emptyMessage?: string;
}

const SearchSelect: React.FC<SearchSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = '선택하세요',
    searchPlaceholder = '검색...',
    disabled = false,
    error,
    label,
    required,
    emptyMessage = '검색 결과가 없습니다'
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [filteredOptions, setFilteredOptions] = useState<SearchSelectOption[]>(options);

    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        if (searchTerm) {
            const filtered = options.filter(opt => {
                const search = searchTerm.toLowerCase();
                return opt.label.toLowerCase().includes(search) ||
                       (opt.subLabel && opt.subLabel.toLowerCase().includes(search));
            });
            setFilteredOptions(filtered);
        } else {
            setFilteredOptions(options);
        }
        setSelectedIndex(-1);
    }, [searchTerm, options]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleOpen = () => {
        if (disabled) return;
        setIsOpen(true);
        setSearchTerm('');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSelect = (option: SearchSelectOption) => {
        onChange(option.value);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[selectedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                break;
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            {label && (
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                    {label} {required && <span className="text-destructive">*</span>}
                </label>
            )}

            {/* Selected Value Display / Trigger */}
            <button
                type="button"
                onClick={handleOpen}
                disabled={disabled}
                className={cn(
                    'flex h-9 w-full items-center justify-between gap-2 rounded border bg-background px-3 text-left text-base transition-colors duration-fast',
                    disabled
                        ? 'cursor-not-allowed bg-neutral-100 text-neutral-400'
                        : 'cursor-pointer hover:border-neutral-400',
                    error ? 'border-destructive' : 'border-input',
                    isOpen && 'border-ring ring-1 ring-ring'
                )}
            >
                <span className={cn('truncate', selectedOption ? 'text-foreground' : 'text-neutral-400')}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown
                    className={cn(
                        'h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-fast',
                        isOpen && 'rotate-180'
                    )}
                />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full overflow-hidden rounded border border-border bg-popover text-popover-foreground">
                    {/* Search Input */}
                    <div className="border-b border-border p-2">
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                className="h-8 w-full rounded border border-input bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-60 overflow-y-auto">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={option.value}
                                    className={cn(
                                        'cursor-pointer px-3 py-2 transition-colors duration-fast',
                                        option.value === value
                                            ? 'bg-navy-50 text-primary'
                                            : selectedIndex === index
                                              ? 'bg-muted'
                                              : 'hover:bg-muted'
                                    )}
                                    onClick={() => handleSelect(option)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                >
                                    <div className="text-base font-medium">{option.label}</div>
                                    {option.subLabel && (
                                        <div className="mt-0.5 text-xs text-muted-foreground">{option.subLabel}</div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                                <SearchX className="mx-auto mb-2 h-6 w-6 text-neutral-300" />
                                {emptyMessage}
                            </div>
                        )}
                    </div>

                    {/* Footer with count */}
                    {options.length > 0 && (
                        <div className="border-t border-border bg-neutral-50 px-3 py-2 text-xs text-muted-foreground tabular-nums">
                            총 {filteredOptions.length}개 {searchTerm && `(전체 ${options.length}개)`}
                        </div>
                    )}
                </div>
            )}

            {error && <span className="mt-1 block text-xs text-destructive">{error}</span>}
        </div>
    );
};

export default SearchSelect;
